/**
 * Resolves the positions at which content should be inserted into the editor
 * for an 'insert' mode editor-suggestion.
 *
 * If explicit positions have been provided (via the `positions` prop), those are
 * used as-is. Otherwise, if the user has never explicitly focused/clicked into
 * the editor, the content is inserted at the end of the document. This ensures
 * that "Insert to page" appends to the end of the page when no cursor has been
 * explicitly placed, while still respecting an explicit click at position 1
 * (the very beginning of the document).
 *
 * @param explicitPositions  - Optional explicit [from, to] positions passed as a prop.
 * @param selectionFrom      - The current `state.selection.from` value.
 * @param selectionTo        - The current `state.selection.to` value.
 * @param docContentSize     - The current `state.doc.content.size` value.
 * @param hasFocused         - Whether the user has explicitly focused/clicked the editor.
 * @returns A [from, to] tuple for use with `insertAIContentAtCurrentPosition`.
 */
export function resolveInsertPositions(
	explicitPositions: [number, number] | undefined,
	selectionFrom: number,
	selectionTo: number,
	docContentSize: number,
	hasFocused: boolean,
): [number, number] {
	if (explicitPositions) {
		return explicitPositions;
	}

	if (!hasFocused) {
		// Insert content at end of doc if cursor isn't focused on page
		return [docContentSize, docContentSize];
	}

	return [selectionFrom, selectionTo];
}
